import { hasPermission } from './permissions/usePermissions';
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Menu, Dropdown, Typography, Button, Switch, Tooltip } from 'antd';
import { UserOutlined, SwapOutlined, DatabaseOutlined } from '@ant-design/icons';
import celerLogo from './celerlogo.svg';
const { Text } = Typography;

const NavigationBar = () => {
  const location = useLocation();
  const user = useSelector(state => state.user);
  const viewMode = useSelector(state => state.viewMode || 'vente');
  const archiveViewMode = useSelector(state => state.archiveViewMode || false);

  const isAuthenticated = !!user;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isManager = user?.IsManager;
  const canAccess = permission => hasPermission(user, permission);

  const toggleViewMode = () => {
    dispatch({ type: 'TOGGLE_VIEW_MODE' });
  };

  const toggleArchiveViewMode = () => {
    console.log('[NavigationBar] Toggling archive view mode. Current state:', archiveViewMode);
    dispatch({ type: 'TOGGLE_ARCHIVE_VIEW_MODE' });
  };

  const handleLogout = () => {
    dispatch({ type: 'LOGOUT' });
    navigate('/login');
  };

  const userDropdownMenu = (
    <Menu>
      <Menu.Item key="logout" onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  const navStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 20px',
    background: '#f8f8f8',
    borderBottom: '1px solid #eaeaea',
    boxShadow: '0 1px 0 rgba(0, 0, 0, 0.1)',
  };

  const navItemStyle = {
    margin: '0 10px',
    color: '#333',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    position: 'relative',
    padding: '5px 0',
    display: 'inline-block',
  };

  const activeIndicatorStyle = {
    position: 'absolute',
    bottom: '-2px',
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: '#000',
    transition: 'all 0.3s ease',
  };

  const brandStyle = {
    display: 'flex',
    alignItems: 'center',
    fontSize: '18px',
  };

  const logoStyle = {
    width: '40px',
    height: 'auto',
    marginRight: '10px',
  };

  const userStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };

  const userIconStyle = {
    fontSize: '24px',
    marginRight: '5px',
    color: '#333',
  };

  const navLinksContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const toggleButtonStyle = {
    marginRight: '20px',
    fontWeight: 'bold',
    height: '32px',
    backgroundColor: viewMode === 'achat' ? '#1890ff' : '#52c41a',
    borderColor: viewMode === 'achat' ? '#1890ff' : '#52c41a',
    color: '#fff',
  };

  const archiveToggleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginRight: '20px',
    padding: '4px 12px',
    borderRadius: '4px',
    backgroundColor: archiveViewMode ? '#fff2e8' : '#f5f5f5',
    border: archiveViewMode ? '1px solid #fa8c16' : '1px solid #d9d9d9',
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav style={navStyle}> 
      <div style={brandStyle}>
        <img src={celerLogo} alt="Celer Logo" style={logoStyle} />
        <span>CELER ERP</span>
      </div>
      
      <div style={navLinksContainerStyle}>
        <Button 
          icon={<SwapOutlined />} 
          onClick={toggleViewMode}
          style={toggleButtonStyle}
        >
          {viewMode === 'vente' ? 'VENTE' : 'ACHAT'}
        </Button>

        {/* Archive View Mode Toggle - Requires Archives / Voir */}
        {hasPermission(user, 'archives') && (
          <Tooltip title={archiveViewMode ? "Mode Archive activé: Les données archivées sont visibles" : "Activer le mode Archive pour voir les données archivées"}>
            <div style={archiveToggleContainerStyle}>
              <DatabaseOutlined style={{ color: archiveViewMode ? '#fa8c16' : '#8c8c8c' }} />
              <Text style={{ fontSize: '12px', color: archiveViewMode ? '#fa8c16' : '#8c8c8c' }}>
                Archive
              </Text>
              <Switch
                size="small"
                checked={archiveViewMode}
                onChange={toggleArchiveViewMode}
                style={{ 
                  backgroundColor: archiveViewMode ? '#fa8c16' : undefined 
                }}
              />
            </div>
          </Tooltip>
        )}

        <Link 
          to="/" 
          style={{
            ...navItemStyle,
            color: isActive('/') ? '#000' : '#333',
          }}
        >
          Home
          {isActive('/') && <div style={activeIndicatorStyle} />}
        </Link>

        {viewMode === 'vente' ? (
          <>
            {/* VENTE MODE NAVIGATION */}
            {canAccess('CanManageQuote') && (
              <Link 
                to="/devis" 
                style={{
                  ...navItemStyle,
                  color: isActive('/devis') ? '#000' : '#333',
                }}
              >
                Devis
                {isActive('/devis') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanManageDeliveryNote') && (
              <Link 
                to="/bonliv" 
                style={{
                  ...navItemStyle,
                  color: isActive('/bonliv') ? '#000' : '#333',
                }}
              >
                Bon de Livraison
                {isActive('/bonliv') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {['preparation', 'control', 'collection', 'deliveryNotes'].some(canAccess) && (
              <Link 
                to="/preparation" 
                style={{
                  ...navItemStyle,
                  color: isActive('/preparation') ? '#000' : '#333',
                }}
              >
                Préparation
                {isActive('/preparation') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanManageInvoice') && (
              <Link 
                to="/facture" 
                style={{
                  ...navItemStyle,
                  color: isActive('/facture') ? '#000' : '#333',
                }}
              >
                Factures
                {isActive('/facture') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanManageExpedition') && (
              <Link 
                to="/exped" 
                style={{
                  ...navItemStyle,
                  color: isActive('/exped') ? '#000' : '#333',
                }}
              >
                Expedition
                {isActive('/exped') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanProcessReturns') && (
              <Link 
                to="/avoir" 
                style={{
                  ...navItemStyle,
                  color: isActive('/avoir') ? '#000' : '#333',
                }}
              >
                Avoirs
                {isActive('/avoir') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanProcessPayments') && (
              <Link 
                to="/reg" 
                style={{
                  ...navItemStyle,
                  color: isActive('/reg') ? '#000' : '#333',
                }}
              >
                Règlements
                {isActive('/reg') && <div style={activeIndicatorStyle} />}
              </Link>
            )}
          </>
        ) : (
          <>
            {/* ACHAT MODE NAVIGATION */}
            {canAccess('CanManageAchatCommande') && (
              <Link 
                to="/achat-commande" 
                style={{
                  ...navItemStyle,
                  color: isActive('/achat-commande') ? '#000' : '#333',
                }}
              >
                Commande
                {isActive('/achat-commande') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanManageAchatReception') && (
              <Link 
                to="/achat-reception" 
                style={{
                  ...navItemStyle,
                  color: isActive('/achat-reception') ? '#000' : '#333',
                }}
              >
                Réception
                {isActive('/achat-reception') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanManageAchatFacture') && (
              <Link 
                to="/facture-achat" 
                style={{
                  ...navItemStyle,
                  color: isActive('/facture-achat') ? '#000' : '#333',
                }}
              >
                Facture
                {isActive('/facture-achat') && <div style={activeIndicatorStyle} />}
              </Link>
            )}

            {canAccess('CanManageAchatAvoir') && (
              <Link 
                to="/avoir-achat" 
                style={{
                  ...navItemStyle,
                  color: isActive('/avoir-achat') ? '#000' : '#333',
                }}
              >
                Avoir
                {isActive('/avoir-achat') && <div style={activeIndicatorStyle} />}
              </Link>
            )}
          </>
        )}

        {isManager && (
          <Link 
            to="/dashboard" 
            style={{
              ...navItemStyle,
              color: isActive('/dashboard') ? '#000' : '#333',
            }}
          >
            Dashboard
            {isActive('/dashboard') && <div style={activeIndicatorStyle} />}
          </Link>
        )}
      </div>

      {isAuthenticated ? (
        <Dropdown overlay={userDropdownMenu} trigger={['click']}>
          <div style={userStyle}>
            <UserOutlined style={userIconStyle} />
            <Text>{user.Name}</Text>
          </div>
        </Dropdown>
      ) : (
        <Link 
          to="/login" 
          style={{
            ...navItemStyle,
            color: isActive('/login') ? '#000' : '#333',
          }}
        >
          Login
          {isActive('/login') && <div style={activeIndicatorStyle} />}
        </Link>
      )}
    </nav>
  );
};

export default NavigationBar;